import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { Modal, Button } from 'react-bootstrap';

interface VersionChangeDetail {
  oldVersion: string;
  newVersion: string;
}

// Global flag to track version mismatch
let versionMismatchDetected = false;
const listeners = new Set<() => void>();

// Subscribe/unsubscribe for useSyncExternalStore
const subscribe = (callback: () => void) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

// Get current state
const getSnapshot = () => versionMismatchDetected;

// Set the flag and notify all subscribers
const setVersionMismatch = (value: boolean) => {
  versionMismatchDetected = value;
  listeners.forEach(listener => listener());
};

// Hook that React components can use to reactively track version mismatch
export const useVersionMismatch = (): boolean => {
  return useSyncExternalStore(subscribe, getSnapshot);
};

const VersionUpdateNotification: React.FC = () => {
  const [show, setShow] = useState(false);
  const [versionInfo, setVersionInfo] = useState<VersionChangeDetail | null>(null);

  useEffect(() => {
    const handleVersionChange = (event: Event) => {
      const customEvent = event as CustomEvent<VersionChangeDetail>;
      setVersionInfo(customEvent.detail);
      setShow(true);
      setVersionMismatch(true); // Set global flag and notify subscribers
    };

    window.addEventListener('app-version-changed', handleVersionChange);

    return () => {
      window.removeEventListener('app-version-changed', handleVersionChange);
    };
  }, []);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>
          <i className="bi bi-arrow-repeat me-2"></i>
          Update Available
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          A new version of the application is available and must be loaded.
        </p>
        {versionInfo && (
          <>
            <p className="mb-1 text-muted small">
              Frontend version: {versionInfo.oldVersion}
            </p>
            <p className="mb-0 text-muted small">
              Backend version: {versionInfo.newVersion}
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleReload}>
          Reload Now
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VersionUpdateNotification;
